import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TPostAttachment } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { MdOutlineInsertDriveFile, MdPictureAsPdf } from 'react-icons/md';

import { useImagePreviewService } from '../dialogs/image-preview';
import { usePdfPreviewService } from '../dialogs/pdf-preview';
import { PostVideoPlayer } from './post-video-player';

interface PostAttachmentListProps {
  attachments: TPostAttachment[];
  // Cap for image/video height; images/videos scale down to fit. Default 300.
  maxHeight?: number;
}

// Open an attachment (image / file) in a new tab via a transient anchor.
const openAttachment = (url: string) => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
};

// A 'file' attachment is a PDF when its mime type says so (or the name ends .pdf).
const isPdfAttachment = (attachment: TPostAttachment): boolean =>
  attachment.mimeType === 'application/pdf' || (attachment.name?.toLowerCase().endsWith('.pdf') ?? false);

/**
 * Read-only display of a post's attachments (images, videos, files). Videos get
 * a full player, files a clickable chip, images a click-to-open thumbnail.
 * Shared by the member post card and the dashboard moderation detail panel so
 * both render media identically.
 */
export const PostAttachmentList = ({ attachments, maxHeight = 300 }: PostAttachmentListProps) => {
  const { openPreview } = useImagePreviewService();
  const { openPreview: openPdfPreview } = usePdfPreviewService();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: 1 }}>
      {attachments.map((attachment, index) => {
        // Stored URLs may be relative paths (the transcoder saves the compressed
        // derivative as `/uploads/…`); getMediaUrl prefixes the CDN base for
        // those and passes absolute originals through unchanged.
        const src = getMediaUrl(attachment.url) ?? attachment.url;
        const poster = getMediaUrl(attachment.posterUrl);

        if (attachment.type === 'video') {
          return (
            <Box key={attachment.url} sx={{ width: '100%' }}>
              <PostVideoPlayer src={src} poster={poster} maxHeight={maxHeight} />
            </Box>
          );
        }

        if (attachment.type === 'file') {
          // PDFs open in the inline lightbox (like images); other files download.
          const pdf = isPdfAttachment(attachment);
          return (
            <Box
              key={attachment.url}
              onClick={() => (pdf ? openPdfPreview(src, attachment.name) : openAttachment(src))}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                px: '12px',
                py: '10px',
                borderRadius: '12px',
                border: '1px solid #e8e8e8',
                bgcolor: '#fafafa',
                cursor: 'pointer',
                maxWidth: '100%',
                '&:hover': { bgcolor: '#f0f0f0' },
              }}
            >
              <Box
                component={pdf ? MdPictureAsPdf : MdOutlineInsertDriveFile}
                sx={{ fontSize: 22, color: pdf ? '#d32f2f' : 'primary.main' }}
              />
              <Typography
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 240,
                }}
              >
                {attachment.name || 'Download file'}
              </Typography>
            </Box>
          );
        }

        return (
          <Box
            key={attachment.url}
            component="img"
            src={src}
            alt={attachment.name || `Attachment ${index + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight,
              borderRadius: '12px',
              objectFit: 'cover',
              cursor: 'pointer',
            }}
            onClick={() => openPreview(src, attachment.name || `Attachment ${index + 1}`)}
          />
        );
      })}
    </Box>
  );
};
